<template>
    <div>
        <vx-selectable
            size="xl"
            :multiple="true"
            :no-tags="true"
            @search="search"
            :placeholder="'Choose a ' + props.resourceType"
            :options="options.filter((option) => !isSelected(option))"
            :modelValue="props.modelValue"
            :clearable="true"
            :relativeDropdown="true"
            :closeOnSelect="false"
            @update:modelValue="emit('update:modelValue', $event)"
        >
            <template v-slot:option="{ option }">
                <div class="flex w-full items-center justify-between text-slate-800 py-3 lg:py-0">
                    <div class="flex items-center gap-2">
                        <div class="line-clamp-2">
                            {{ option.label }}
                        </div>
                        <div v-if="props.resourceType === 'segment'">
                            <segment-description-popover :modelValue="option"></segment-description-popover>
                        </div>
                        <div v-if="props.resourceType === 'campaign'">
                            <resource-details-popover
                                :modelValue="{ ...option, resource_type: 'campaign' }"
                                :teamId="teamId"
                            />
                        </div>
                    </div>
                    <div class="flex gap-4 lg:gap-8">
                        <div>
                            <resource-selectable-info
                                :color="false"
                                :resource="option"
                                :resourceType="props.resourceType"
                                :iconColor="appearance.icon"
                                :segmentContactsCount="segmentContactsCount[option.id]"
                            ></resource-selectable-info>
                        </div>

                        <vx-button type="button" color="muted" size="xs">
                            Add
                            <font-awesome-icon widthAuto class="text-xs ml-1" :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </template>
        </vx-selectable>

        <div v-if="props.modelValue.length" class="mt-6">
            <div
                data-test="resource-selected"
                v-for="resource in props.modelValue"
                :key="resource.id"
                tabindex="0"
                role="button"
                :class="appearance.row"
                class="appearance-none border border-solid -mt-px first:rounded-t-lg last:rounded-b-lg flex items-center justify-between px-4 text-base lg:min-h-14"
                @click="remove(resource)"
                @keydown.enter="remove(resource)"
                @keydown.space="remove(resource)"
            >
                <div class="flex w-full items-center justify-between py-3 lg:py-0">
                    <div class="flex items-center gap-2">
                        <div class="line-clamp-2">
                            {{ resource.name }}
                        </div>
                        <div v-if="props.resourceType === 'segment'">
                            <segment-description-popover :modelValue="resource"></segment-description-popover>
                        </div>
                        <div v-if="props.resourceType === 'campaign'">
                            <resource-details-popover
                                :modelValue="{ ...resource, resource_type: 'campaign' }"
                                :teamId="teamId"
                            />
                        </div>
                    </div>
                    <div class="shrink-0 flex items-center gap-4 lg:gap-8">
                        <resource-selectable-info
                            :resource="resource"
                            :resourceType="props.resourceType"
                            :color="true"
                            :iconColor="appearance.icon"
                            :segmentContactsCount="segmentContactsCount[resource.id]"
                        ></resource-selectable-info>

                        <button
                            :class="appearance.button"
                            class="appearance-none rounded px-2 text-xs h-6 text-black font-semibold"
                        >
                            Remove
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxSelectable } from '@voxie/frontend-components';
import { computed, onMounted, ref } from 'vue';
import { automationRulesClient, campaignsClient, segmentsClient } from '~/services';
import debounce from '~/utils/debounce';
import ResourceSelectableInfo from './ResourceSelectableInfo.vue';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import SegmentDescriptionPopover from '~/components/contacts/segments/SegmentDescriptionPopover.vue';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    modelValue: {
        type: Array,
        required: false,
        default: () => [],
    },
    resourceType: {
        type: String,
        validator: (resourceType) => ['automation', 'campaign', 'segment'].includes(resourceType),
    },
});
const emit = defineEmits(['update:modelValue']);

const { contactsCount: segmentContactsCount, fetchSegmentsContactsCount } = useSegmentsContactsCount();

const options = ref([]);

const isSelected = (resource) => {
    return Boolean(props.modelValue.find((item) => Number(item.id) === Number(resource.id)));
};

const remove = async (resource) => {
    emit(
        'update:modelValue',
        props.modelValue.filter((item) => Number(item.id) !== Number(resource.id))
    );
};

const search = debounce(async (search, loading) => {
    loading?.(true);

    const params = {
        sort_field: 'id',
        sort_dir: 'desc',
        per_page: 10,
        query: search,
        [props.resourceType === 'segment' ? 'filter[flow]' : 'flow']: {
            in_flow: 0,
        },
    };

    try {
        let client;

        if (props.resourceType === 'automation') {
            client = automationRulesClient.search(teamId, params);
        } else if (props.resourceType === 'campaign') {
            client = campaignsClient.campaignsGetRecords(teamId, params);
        } else if (props.resourceType === 'segment') {
            client = segmentsClient.getSegments({ teamId }, { ...params, 'filter[is_template]': 0 });
        }

        const response = await client;
        options.value = response.data.data.map((resource) => {
            return {
                ...resource,
                label: `${resource.id} - ${resource.name}`,
            };
        });

        if (props.resourceType === 'segment') {
            fetchSegmentsContactsCount(
                { teamId },
                options.value.map((segment) => segment.id)
            );
        }
    } finally {
        loading?.(false);
    }
}, 300);

onMounted(() => search());

const appearance = computed(() => {
    if (props.resourceType === 'automation') {
        return {
            button: 'bg-amber-200',
            row: 'text-amber-800 border-amber-300 bg-amber-100',
            icon: 'text-amber-500',
        };
    }
    if (props.resourceType === 'campaign') {
        return {
            button: 'bg-sky-200',
            row: 'text-sky-800 border-sky-300 bg-sky-100',
            icon: 'text-sky-500',
        };
    }
    if (props.resourceType === 'segment') {
        return {
            button: 'bg-violet-200',
            row: 'text-violet-800 border-violet-300 bg-violet-100',
            icon: 'text-violet-500',
        };
    }

    return {};
});
</script>
